import {
  GATSBY_CONF_SHOP_NAME,
  GATSBY_ENV_COUNTRY_ISO3166,
} from "@src/utils/constants";
import { GATSBY_CURRENCY_CODE } from "@src/backbone/prototypes/formatPrice";
import ApiRequest from "@src/backbone/prototypes/ApiRequest";

// web: safari only; mobile: ios only
export const APPLE_PAY_MERCHANT_ID =
  process.env.GATSBY_CONF_APPLE_PAY_MERCHANT_ID || "merchant.sushishop.fr";

const CsvTrimMap = (csv: string, sep = ",") => {
  return csv
    .split(sep)
    .map((network) => network.trim())
    .filter((n: string) => !!n);
};

export const APPLE_PAY_SUPPORTED_NETWORKS = CsvTrimMap(
  process.env.GATSBY_CONF_APPLE_PAY_SUPPORTED_NETWORKS || "visa,masterCard"
);

export const GOOGLE_PAY_SUPPORTED_NETWORKS = CsvTrimMap(
  process.env.GATSBY_CONF_GOOGLE_PAY_ALLOWED_CARD_NETWORKS || "VISA,MASTERCARD"
) as google.payments.api.CardNetwork[];
export const checkApplePayDevice = () => {
  return true;
};
export enum ApplePayPaymentCredentialStatus {
  /**
   * Confirms that the device supports Apple Pay and there’s at least one active payment credential in Wallet that qualifies for payments on the web. Show an Apple Pay button and offer Apple Pay as the primary, but not necessarily sole, payment option.
   */
  CREDENTIALS_AVAILABLE = "paymentCredentialsAvailable",
  /**
   * Confirms that the device supports Apple Pay, but the Wallet information is unknown. Show an Apple Pay button and offer Apple Pay as a possible payment option.
   */
  CREDENTIALS_UNKNOWN = "paymentCredentialStatusUnknown",
  /**
   * Confirms that the device supports Apple Pay and that the device doesn’t have any active payment credentials in Wallet. Offer Apple Pay as a payment option, but don’t make it the sole or default payment option. This gives people the option to set up Apple Pay as part of their purchase.
   */
  CREDENTIALS_UNAVAILABLE = "paymentCredentialsUnavailable",
  /**
   * Indicates that the device doesn’t support Apple Pay. Don’t display an Apple Pay button or offer Apple Pay as a payment option.
   */
  APPLE_PAY_UNSUPPORTED = "applePayUnsupported",
}

export const isApplePaySdkReady = () => {
  return (
    typeof window !== "undefined" &&
    // @ts-ignore
    !!window.ApplePaySession &&
    // @ts-ignore
    !!window.ApplePaySession.applePayCapabilities
  );
};

export const isGooglePaySdkReady = () => {
  return (
    typeof window !== "undefined" &&
    // @ts-ignore
    !!window.google &&
    // @ts-ignore
    !!window.google.payments
  );
};

export const ApplePaySessionGlobal = () => {
  return typeof window !== "undefined"
    ? // @ts-ignore
      (window.ApplePaySession as typeof ApplePaySession)
    : undefined;
};

const applePaySdkScriptId = "apple-pay-sdk";

const LoadScript = (
  url: string,
  id: string,
  onLoad: () => void,
  onError = () => {},
  crossOrigin: string | null = null,
  async = true
) => {
  let script: HTMLScriptElement = document.createElement(
    "script"
  ) as HTMLScriptElement;
  script.id = id;
  script.src = url;
  script.async = async;
  script.onload = onLoad;
  script.onerror = onError;
  if (crossOrigin !== null) {
    script.crossOrigin = crossOrigin;
  }
  document.body.appendChild(script);
  return script;
};

export const LoadApplePaySdk = async (hasApplePay: boolean) => {
  return new Promise<ApplePayPaymentCredentialStatus>((resolve, reject) => {
    const ApplePaySdkJsUrl =
      "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
    let script: HTMLScriptElement = document.getElementById(
      applePaySdkScriptId
    ) as HTMLScriptElement;
    const onSdkReady = () => {
        try {
          // @ts-ignore
          ApplePaySession.applePayCapabilities(APPLE_PAY_MERCHANT_ID)
            // @ts-ignore
            .then((capabilities) => {
              resolve(
                capabilities.paymentCredentialStatus as ApplePayPaymentCredentialStatus // ApplePayJS.PaymentCredentialStatus
              );
            })
            // @ts-ignore
            .catch((e) => {
              console.error(e);
              reject();
            });
        } catch (e) {
          reject();
        }
      },
      fail = () => {
        reject();
      };
    if (!hasApplePay) {
      reject();
      return;
    } else if (isApplePaySdkReady()) {
      onSdkReady();
    } else if (!script) {
      LoadScript(ApplePaySdkJsUrl, applePaySdkScriptId, onSdkReady, fail, "");
    } else {
      script.onload = onSdkReady;
      script.onerror = fail;
    }
  });
};

type ApplePayPaymentRequestProps = {
  amount: number;
};

export type ApplePayDoPaymentPayload = {
  encryptedData: string;
  ephemeralKey: string;
  publicKeyHash: string;
  amount: number;
};

export enum ApplePayPaymentStatus {
  UNSUPPORTED = "UNSUPPORTED",
  ERROR = "ERROR",
  CANCELED = "CANCELED",
}
export const StartApplePayPayment = async ({
  amount,
}: ApplePayPaymentRequestProps) => {
  return new Promise<{
    session: ApplePaySession;
    payload: ApplePayDoPaymentPayload;
  }>((resolve, reject) => {
    // @ts-ignore
    if (!!ApplePaySession && ApplePaySession.applePayCapabilities) {
      const request: any = {
        countryCode: GATSBY_ENV_COUNTRY_ISO3166,
        currencyCode: GATSBY_CURRENCY_CODE,
        // todo : supportedNetworks by Store ?
        supportedNetworks: APPLE_PAY_SUPPORTED_NETWORKS, // , "amex" is not yet supported
        merchantCapabilities: ["supports3DS"],
        // todo, support Edenred partial payment
        total: { label: GATSBY_CONF_SHOP_NAME, amount },
      };
      const session = new ApplePaySession(6, request);
      session.oncancel = function (e) {
        console.warn("Apple Pay payment canceled", e);
        reject({ statusCode: ApplePayPaymentStatus.CANCELED });
      };
      session.onpaymentmethodselected = function (e) {
        console.log("onpaymentmethodselected ApplePay", e);
        const update: ApplePayJS.ApplePayPaymentMethodUpdate = {
          newTotal: { label: GATSBY_CONF_SHOP_NAME, amount: `${amount}` },
          newLineItems: [{ label: "Subtotal", amount: `${amount}` }],
        };
        session.completePaymentMethodSelection(update);
      };
      session.onvalidatemerchant = function (e) {
        ApiRequest(
          "ToolsPaymentMethod",
          {
            validationUrl: e.validationURL,
          },
          {
            method: "applePaySession",
          }
        )
          .then((data) => {
            session.completeMerchantValidation(data);
          })
          .catch((e: any) => {
            session.abort();
            reject({ ...e, statusCode: ApplePayPaymentStatus.ERROR });
          });
      };
      session.onpaymentauthorized = function (e) {
        const paymentData = e.payment.token.paymentData;
        const {
          data: encryptedData,
          header: { publicKeyHash, ephemeralPublicKey: ephemeralKey },
        } = paymentData;
        const payload: ApplePayDoPaymentPayload = {
          encryptedData,
          ephemeralKey,
          publicKeyHash,
          amount,
        };
        resolve({
          session,
          payload,
        });
      };
      session.begin();
    } else {
      reject({
        statusCode: ApplePayPaymentStatus.UNSUPPORTED,
      });
    }
  });
};

const GOOGLE_PAY_ENVIRONMENT =
  (process.env
    .GATSBY_CONF_GOOGLE_PAY_ENVIRONMENT as google.payments.api.Environment) ||
  "PRODUCTION";
const GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS = CsvTrimMap(
  (process.env.GATSBY_CONF_GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS as string) ||
    "CRYPTOGRAM_3DS"
) as google.payments.api.CardAuthMethod[];

type GooglePaymentDataRequestProps = {
  totalPrice: number;
  gatewayMerchantId?: string;
};

export const LoadGooglePaySdk = async (hasGooglePay: boolean) => {
  return new Promise<GooglePayProps>((resolve, reject) => {
    const googlePaySDKUrl = "https://pay.google.com/gp/p/js/pay.js",
      idScriptSdk = "google-pay-sdk";
    const script = document.getElementById(idScriptSdk);
    const onFail = () => {
      console.warn("Google Pay SDK load failed");
      reject();
    };
    const onSuccess = () => {
      const paymentsClient = new google.payments.api.PaymentsClient({
        environment: GOOGLE_PAY_ENVIRONMENT,
      });
      const baseRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
      };
      const allowedCardAuthMethods: google.payments.api.CardAuthMethod[] =
        GOOGLE_PAY_ALLOWED_CARD_AUTH_METHODS;

      const baseCardPaymentMethod: google.payments.api.IsReadyToPayPaymentMethodSpecification =
        {
          type: "CARD",
          parameters: {
            allowedAuthMethods: allowedCardAuthMethods,
            allowedCardNetworks: GOOGLE_PAY_SUPPORTED_NETWORKS,
          },
        };
      const isReadyToPayRequest: google.payments.api.IsReadyToPayRequest = {
        ...baseRequest,
        ...{
          allowedPaymentMethods: [baseCardPaymentMethod],
          existingPaymentMethodRequired: true, // pour accepter uniquement les cartes déjà enregistrées
        },
      };
      const getGooglePaymentDataRequest = ({
        totalPrice,
        gatewayMerchantId = "",
      }: GooglePaymentDataRequestProps) => {
        const GOOGLE_PAY_MERCHANT_ID =
          process.env.GATSBY_CONF_GOOGLE_PAY_MERCHANT_ID || "";
        const GOOGLE_PAY_MERCHANT_NAME =
          process.env.GATSBY_CONF_GOOGLE_PAY_MERCHANT_NAME || "";
        const tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification =
          {
            type: "PAYMENT_GATEWAY",
            parameters: {
              gateway: "worldlineingenicoogone",
              gatewayMerchantId,
            },
          };
        const cardPaymentMethod: google.payments.api.PaymentMethodSpecification =
          {
            ...baseCardPaymentMethod,
            tokenizationSpecification,
          };
        const paymentDataRequest: google.payments.api.PaymentDataRequest = {
          ...baseRequest,
          allowedPaymentMethods: [cardPaymentMethod],
          transactionInfo: {
            totalPriceStatus: "FINAL",
            totalPriceLabel: GATSBY_CONF_SHOP_NAME,
            totalPrice: totalPrice.toFixed(2),
            currencyCode: GATSBY_CURRENCY_CODE,
            countryCode: GATSBY_ENV_COUNTRY_ISO3166,
          },
          merchantInfo: {
            merchantId: GOOGLE_PAY_MERCHANT_ID,
            merchantName: GOOGLE_PAY_MERCHANT_NAME,
          },
        };
        return paymentDataRequest;
      };

      const prefetchGooglePaymentData = (
        props: GooglePaymentDataRequestProps
      ) => {
        const paymentDataRequest = getGooglePaymentDataRequest(props);
        paymentsClient.prefetchPaymentData(paymentDataRequest);
      };

      const startPaymentDataRequest = (
        props: GooglePaymentDataRequestProps
      ) => {
        const paymentDataRequest = getGooglePaymentDataRequest(props);
        return paymentsClient.loadPaymentData(paymentDataRequest);
      };
      paymentsClient
        .isReadyToPay(isReadyToPayRequest)
        .then(function (response) {
          console.log("Google Pay SDK loaded", response);
          if (response.result && response.paymentMethodPresent) {
            const googlePayProps: GooglePayProps = {
              paymentsClient,
              baseCardPaymentMethod,
              prefetchGooglePaymentData,
              startPaymentDataRequest,
            };
            resolve(googlePayProps);
            // @todo prefetch payment data to improve performance after confirming site functionality
            // prefetchGooglePaymentData();
          } else {
            reject();
          }
        })
        .catch(function (err) {
          // show error in developer console for debugging
          console.warn("Google Pay SDK loaded but unavailable");
          reject();
        });
    };
    if (!hasGooglePay) {
      reject();
    } else if (isGooglePaySdkReady()) {
      onSuccess();
    } else if (!script) {
      LoadScript(googlePaySDKUrl, idScriptSdk, onSuccess, onFail, null, false);
    } else {
      script.onload = onSuccess;
      script.onerror = onFail;
    }
  });
};
export type GooglePayProps = {
  paymentsClient: google.payments.api.PaymentsClient;
  baseCardPaymentMethod: google.payments.api.IsReadyToPayPaymentMethodSpecification;
  prefetchGooglePaymentData: (props: GooglePaymentDataRequestProps) => void;
  startPaymentDataRequest: (
    props: GooglePaymentDataRequestProps
  ) => Promise<google.payments.api.PaymentData>;
};
